import { useState } from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { GitPullRequest, Link2, Plus, Trash2, UserRound } from "lucide-react";

import { DELIVERY_STATUS_LABEL, IMPL_KIND_LABEL } from "@/presentation/data/board";
import { cn } from "@monorepo-apps/ui/src/lib/utils";
import type {
  BoardActivityAction,
  BoardActivityRow,
  DeliveryStatus,
  MilestoneRow,
  MvpComponentRow,
  WorkstreamRow,
} from "@/infrastructure/supabase/database.types";

export interface ActivityLookups {
  stakeholdersById: Map<string, string>;
  componentsById: Map<string, MvpComponentRow>;
  milestonesByKey: Map<string, MilestoneRow>;
  workstreamsByKey: Map<string, WorkstreamRow>;
}

interface BoardActivityFeedProps {
  entries: BoardActivityRow[];
  isLoading: boolean;
  lookups: ActivityLookups;
  /** Show the BF-### chip (whole-board feed); per-item feeds omit it. */
  showItem?: boolean;
  onOpenItem?: (featureId: string) => void;
  /** Keyset pagination — more older rows are available. */
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const FIELD_LABEL: Record<string, string> = {
  title: "Title",
  description: "Description",
  delivery_status: "Status",
  milestone_key: "Milestone",
  impl_kind: "Implementation",
  owner_id: "Owner",
  mvp_blocking: "Critical path",
  due_date: "Due date",
  component_id: "Component",
  workstream_key: "Workstream",
  notes: "Notes",
  acceptance_md: "Acceptance",
  pr_url: "PR",
  branch_name: "Branch",
};

// Long free-text fields: show "updated", not a noisy old→new dump.
const LONG_FIELDS = new Set(["notes", "description", "acceptance_md"]);

export function BoardActivityFeed({
  entries,
  isLoading,
  lookups,
  showItem = false,
  onOpenItem,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: BoardActivityFeedProps) {
  if (isLoading) {
    return <p className="text-muted-foreground px-1 py-3 text-[12px] italic">Loading activity…</p>;
  }
  if (entries.length === 0) {
    return <p className="text-muted-foreground px-1 py-3 text-[12px] italic">No activity yet.</p>;
  }

  return (
    <ol className="flex flex-col">
      {entries.map((e) => (
        <ActivityRow
          key={e.id}
          e={e}
          lookups={lookups}
          showItem={showItem}
          onOpenItem={onOpenItem}
        />
      ))}
      {hasMore ? (
        <li className="pt-2">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="text-muted-foreground hover:text-foreground hover:bg-muted/50 w-full rounded-sm py-1.5 text-[12px] transition-colors disabled:opacity-60"
          >
            {isLoadingMore ? "Loading…" : "Load more"}
          </button>
        </li>
      ) : null}
    </ol>
  );
}

function ActivityRow({
  e,
  lookups,
  showItem,
  onOpenItem,
}: {
  e: BoardActivityRow;
  lookups: ActivityLookups;
  showItem: boolean;
  onOpenItem?: (featureId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  // Long free-text fields keep the line scannable: a "show diff" toggle reveals
  // the full before/after (which IS stored) on demand, instead of dumping it.
  const isLong = e.action === "update" && !!e.field && LONG_FIELDS.has(e.field);
  const label = e.field ? (FIELD_LABEL[e.field] ?? e.field) : "field";

  return (
    <li className="border-border/50 flex items-start gap-2.5 border-b py-2.5 last:border-b-0">
      <ActionIcon action={e.action} field={e.field} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          {showItem && e.feature_code ? (
            <button
              type="button"
              onClick={() => e.feature_id && onOpenItem?.(e.feature_id)}
              className="font-data text-primary shrink-0 text-[10.5px] tabular-nums hover:underline"
            >
              {e.feature_code}
            </button>
          ) : null}
          <span className="text-foreground text-[12.5px] leading-snug">
            {isLong ? (
              <>
                <span className="font-medium">{label}</span>{" "}
                <button
                  type="button"
                  onClick={() => setOpen((o) => !o)}
                  aria-expanded={open}
                  className="text-primary text-[11.5px] hover:underline"
                >
                  {open ? "hide diff" : "show diff"}
                </button>
              </>
            ) : (
              renderDetail(e, lookups)
            )}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-1.5">
          <span className="text-muted-foreground truncate text-[11px] font-medium">{e.actor}</span>
          <span className="text-muted-foreground/50 text-[10px]">·</span>
          <time
            className="text-muted-foreground/70 text-[10.5px]"
            dateTime={e.created_at}
            title={new Date(e.created_at).toLocaleString()}
          >
            {formatDistanceToNowStrict(new Date(e.created_at), { addSuffix: true })}
          </time>
        </div>
        {isLong && open ? (
          <div className="mt-2 flex flex-col gap-1.5">
            <DiffBlock tone="removed" value={e.old_value} />
            <DiffBlock tone="added" value={e.new_value} />
          </div>
        ) : null}
      </div>
    </li>
  );
}

/** A long field's before / after, shown on demand (also used by the B2C board feed). */
export function DiffBlock({ tone, value }: { tone: "removed" | "added"; value: string | null }) {
  const empty = value === null || value === "";
  return (
    <div
      className={cn(
        "rounded-sm border-l-2 py-1.5 pr-2 pl-2.5",
        tone === "removed"
          ? "border-[var(--rag-red)] bg-[oklch(0.6_0.18_25_/_0.06)]"
          : "border-[var(--rag-green)] bg-[oklch(0.66_0.16_155_/_0.06)]"
      )}
    >
      <span className="text-muted-foreground/70 mb-0.5 block text-[9.5px] tracking-wider uppercase">
        {tone === "removed" ? "before" : "after"}
      </span>
      <pre
        className={cn(
          "max-h-48 overflow-auto font-sans text-[12px] leading-snug whitespace-pre-wrap",
          empty ? "text-muted-foreground italic" : "text-foreground"
        )}
      >
        {empty ? "(empty)" : value}
      </pre>
    </div>
  );
}

function ActionIcon({ action, field }: { action: BoardActivityAction; field: string | null }) {
  const base = "mt-0.5 size-3.5 shrink-0";
  if (action === "create") return <Plus className={cn(base, "text-[var(--rag-green)]")} />;
  if (action === "delete") return <Trash2 className={cn(base, "text-[var(--rag-red)]")} />;
  if (action === "dep_add" || action === "dep_remove")
    return <Link2 className={cn(base, "text-[oklch(0.55_0.22_300)]")} />;
  if (field === "pr_url" || field === "branch_name")
    return <GitPullRequest className={cn(base, "text-primary")} />;
  if (field === "owner_id") return <UserRound className={cn(base, "text-muted-foreground")} />;
  return <span className={cn(base, "bg-muted-foreground/40 mt-1.5 size-1.5 rounded-full")} />;
}

/** Resolve a raw stored value to a human label using the board's lookups. */
function pretty(field: string | null, value: string | null, lk: ActivityLookups): string {
  if (value === null || value === "") return "—";
  switch (field) {
    case "delivery_status":
      return DELIVERY_STATUS_LABEL[value as DeliveryStatus] ?? value;
    case "impl_kind":
      return IMPL_KIND_LABEL[value as keyof typeof IMPL_KIND_LABEL] ?? value;
    case "owner_id":
      return lk.stakeholdersById.get(value) ?? "someone";
    case "component_id":
      return lk.componentsById.get(value)?.name_en ?? value;
    case "milestone_key":
      return lk.milestonesByKey.get(value)?.code ?? value;
    case "workstream_key": {
      const w = lk.workstreamsByKey.get(value);
      return w ? `Block ${w.code}` : value;
    }
    case "mvp_blocking":
      return value === "true" ? "on" : "off";
    default:
      return value;
  }
}

function renderDetail(e: BoardActivityRow, lk: ActivityLookups) {
  switch (e.action) {
    case "create":
      return <span>created this item</span>;
    case "delete":
      return <span>deleted this item</span>;
    case "dep_add":
      return (
        <span>
          added dependency <span className="font-data text-[11.5px]">→ {e.new_value ?? "?"}</span>
        </span>
      );
    case "dep_remove":
      return (
        <span>
          removed dependency <span className="font-data text-[11.5px]">→ {e.old_value ?? "?"}</span>
        </span>
      );
    case "update": {
      // Long free-text fields are rendered by ActivityRow (expandable diff).
      const label = e.field ? (FIELD_LABEL[e.field] ?? e.field) : "field";
      return (
        <span>
          <span className="font-medium">{label}</span>{" "}
          <span className="text-muted-foreground">{pretty(e.field, e.old_value, lk)}</span>
          <span className="text-muted-foreground/60"> → </span>
          <span>{pretty(e.field, e.new_value, lk)}</span>
        </span>
      );
    }
  }
}
